import { formatInr } from "@/utils/basicFunctions.util";
import { useState, type KeyboardEvent } from "react";
import type { Tier } from "../../../data";

interface UseTierStudioInput {
  tiers: Tier[];
  initialSpend: number;
  moreToReachTemplate: string;
  topTierText: string;
}

// Everything the tier studio shows is derived from one number - the
// customer's total spend. Clicking a tier card jumps the spend to that tier's
// demo amount; the slider sets it directly.
export function useTierStudio({
  tiers,
  initialSpend,
  moreToReachTemplate,
  topTierText,
}: UseTierStudioInput) {
  const initialState = { spend: initialSpend };
  const [state, setState] = useState(initialState);

  const setSpend = (spend: number) =>
    setState((state) => ({ ...state, spend }));

  // Highest tier whose threshold the spend has reached.
  const activeIndex = tiers.reduce(
    (found, tier, index) => (state.spend >= tier.at ? index : found),
    0,
  );
  const tier = tiers[activeIndex];
  const nextTier = tiers[activeIndex + 1];

  const progress = tier.next
    ? Math.min(100, (state.spend / tier.next) * 100)
    : 100;
  const amountText = tier.next
    ? `${formatInr(state.spend)} / ${formatInr(tier.next)}`
    : formatInr(state.spend);
  const moreText =
    tier.next && nextTier
      ? moreToReachTemplate
          .replace("{amount}", formatInr(tier.next - state.spend))
          .replace("{tier}", nextTier.name.toUpperCase())
      : topTierText;

  const selectTier = (index: number) => setSpend(tiers[index].demoSpend);

  // Roving tabs: arrow keys move between tier cards (and select them),
  // matching the WAI-ARIA tabs pattern.
  const onTabKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    const step =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
    if (!step) return;
    event.preventDefault();
    const nextIndex = (index + step + tiers.length) % tiers.length;
    selectTier(nextIndex);
    const tablist = event.currentTarget.parentElement;
    const nextTab = tablist?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[
      nextIndex
    ];
    nextTab?.focus();
  };

  return {
    spend: state.spend,
    setSpend,
    activeIndex,
    tier,
    progress,
    amountText,
    moreText,
    selectTier,
    onTabKeyDown,
  };
}
